/**
 * 🚀 寵物相關計算工具函數（集中管理，避免重複計算）
 */

export const calculateAgeFromBirthDate = (birthDate) => {
  if (!birthDate) return null;
  const today = new Date();
  const birth = new Date(birthDate);
  if (birth > today) return null;

  let years = today.getFullYear() - birth.getFullYear();
  let months = today.getMonth() - birth.getMonth();

  if (months < 0) { years--; months += 12; }
  if (today.getDate() < birth.getDate()) {
    months--;
    if (months < 0) { years--; months += 12; }
  }

  return { years, months };
};

export const formatAge = (pet, t) => {
  if (pet.birth_date) {
    const age = calculateAgeFromBirthDate(pet.birth_date);
    if (!age) return t('common.unknown');

    const yearsText  = t('common.time.years')  || 'years';
    const yearText   = t('common.time.year')   || 'year';
    const monthsText = t('common.time.months') || 'months';
    const monthText  = t('common.time.month')  || 'month';

    if (age.years === 0) {
      return age.months === 1 ? `1 ${monthText}` : `${age.months} ${monthsText}`;
    } else if (age.months === 0) {
      return age.years === 1 ? `1 ${yearText}` : `${age.years} ${yearsText}`;
    }
    return `${age.years} ${age.years === 1 ? yearText : yearsText} ${age.months} ${age.months === 1 ? monthText : monthsText}`;
  }
  if (pet.age) return `${pet.age} ${t('common.time.years') || 'years'}`;
  return t('common.unknown');
};

export const getMealLabel = (time, t) => {
  if (!time) return '';
  const hour = parseInt(time.split(':')[0], 10);
  if (isNaN(hour)) return '';
  if (hour >= 5  && hour < 11) return t('petProfile.breakfast');
  if (hour >= 11 && hour < 17) return t('petProfile.lunch');
  if (hour >= 17 && hour < 23) return t('petProfile.dinner');
  return t('petProfile.snack');
};

export const getMedicationLabel = (time, t) => {
  if (!time) return '';
  const hour = parseInt(time.split(':')[0], 10);
  if (isNaN(hour)) return '';
  if (hour >= 5  && hour < 12) return t('petProfile.morningMeds');
  if (hour >= 12 && hour < 17) return t('petProfile.afternoonMeds');
  if (hour >= 17 && hour < 22) return t('petProfile.eveningMeds');
  return t('petProfile.nightMeds');
};

// ✅ 靜態頻率標籤對應表，移到模組層級
const FREQUENCY_KEY_MAP = {
  Daily:   'petProfile.daily',
  BID:     'petProfile.twiceDaily',
  TID:     'petProfile.thriceDaily',
  Weekly:  'petProfile.weekly',
  Monthly: 'petProfile.monthly',
  Custom:  'petProfile.custom',
};

// ✅ 新增：getFrequencyLabel 移到模組層級，PetProfilePage 不再每次 render 重建
export const getFrequencyLabel = (freq, t) => {
  if (!freq) return '';
  const key = FREQUENCY_KEY_MAP[freq];
  return key ? t(key) : freq;
};

export const getNextFeeding = (pet, t) => {
  if (!pet.feeding_plan || !Array.isArray(pet.feeding_plan) || pet.feeding_plan.length === 0) {
    return pet.feeding_schedule || t('dashboard.notSet');
  }

  const now = new Date();
  const currentTime = now.getHours() * 60 + now.getMinutes();
  const sortedMeals = [...pet.feeding_plan].sort((a, b) => {
    const [h1, m1] = a.time.split(':').map(Number);
    const [h2, m2] = b.time.split(':').map(Number);
    return (h1 * 60 + m1) - (h2 * 60 + m2);
  });

  const todayStr    = t('common.time.today')    || 'Today';
  const tomorrowStr = t('common.time.tomorrow') || 'Tomorrow';

  const nextMeal = sortedMeals.find(meal => {
    const [h, m] = meal.time.split(':').map(Number);
    return (h * 60 + m) > currentTime;
  });

  if (nextMeal) return `${todayStr} ${nextMeal.time} (${getMealLabel(nextMeal.time, t)})`;
  const firstMeal = sortedMeals[0];
  if (firstMeal) return `${tomorrowStr} ${firstMeal.time} (${getMealLabel(firstMeal.time, t)})`;
  return t('dashboard.notSet');
};

export const getNextMedication = (pet, t) => {
  if (!pet.medication_plan || !Array.isArray(pet.medication_plan) || pet.medication_plan.length === 0) {
    return pet.medication_schedule || t('dashboard.notSet');
  }

  const now = new Date();
  const currentTime = now.getHours() * 60 + now.getMinutes();

  const allMedTimes = [];
  pet.medication_plan.forEach(med => {
    if (med.times && Array.isArray(med.times)) {
      med.times.forEach(time => allMedTimes.push({ time, name: med.name }));
    }
  });

  if (allMedTimes.length === 0) return pet.medication_schedule || t('dashboard.notSet');

  allMedTimes.sort((a, b) => {
    const [h1, m1] = a.time.split(':').map(Number);
    const [h2, m2] = b.time.split(':').map(Number);
    return (h1 * 60 + m1) - (h2 * 60 + m2);
  });

  const todayStr    = t('common.time.today')    || 'Today';
  const tomorrowStr = t('common.time.tomorrow') || 'Tomorrow';

  const nextMed = allMedTimes.find(m => {
    const [h, min] = m.time.split(':').map(Number);
    return (h * 60 + min) > currentTime;
  });

  if (nextMed) return `${todayStr} ${nextMed.time} (${getMedicationLabel(nextMed.time, t)})`;
  const firstMed = allMedTimes[0];
  if (firstMed) return `${tomorrowStr} ${firstMed.time} (${getMedicationLabel(firstMed.time, t)})`;
  return t('dashboard.notSet');
};

export const precomputePetSchedules = (pets, t) => {
  return pets.map(pet => ({
    ...pet,
    _computed: {
      nextFeeding:    getNextFeeding(pet, t),
      nextMedication: getNextMedication(pet, t),
      age:            formatAge(pet, t),
    },
  }));
};